import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Bell, X, Zap, Loader2, CheckCheck, RefreshCw, Gift, AlertTriangle, Package } from "lucide-react";
import { NeonButton } from "@/components/Holo";
import api, { formatApiErrorDetail } from "@/lib/api";
import { startCheckout } from "@/lib/billing";

const ICONS = {
  low_credit: { i: AlertTriangle, c: "#eaff00" },
  auto_recharge: { i: RefreshCw, c: "#00f0ff" },
  cap_reached: { i: AlertTriangle, c: "#eaff00" },
  purchase: { i: Package, c: "#39ff14" },
  failed_payment: { i: AlertTriangle, c: "#ff007f" },
  reengagement: { i: Gift, c: "#b026ff" },
};

export default function Notifications() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    api.get("/notifications").then(({ data }) => {
      setItems(data);
      const prompt = data.find((n) => n.type === "auto_recharge" && !n.read);
      if (prompt) setConfirm((c) => c || prompt);
    }).catch(() => {});
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);

  const unread = items.filter((n) => !n.read).length;
  const markRead = async (id) => { await api.post(`/notifications/${id}/read`).catch(() => {}); setItems((p) => p.map((n) => n.id === id ? { ...n, read: true } : n)); };
  const markAll = async () => { await api.post("/notifications/read-all").catch(() => {}); setItems((p) => p.map((n) => ({ ...n, read: true }))); };

  const doRecharge = async () => {
    if (!confirm?.meta?.pack_id) return;
    setLoading(true);
    try { await markRead(confirm.id); await startCheckout({ kind: "credits", pack_id: confirm.meta.pack_id, auto: true }); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setLoading(false); }
  };

  return (
    <>
      <div className="fixed top-4 right-4 z-30">
        <button onClick={() => { setOpen((o) => !o); load(); }} data-testid="notifications-bell"
          className="relative glass border border-white/15 w-10 h-10 flex items-center justify-center hover:border-neon-turquoise transition-colors">
          <Bell size={17} className="text-white/70" />
          {unread > 0 && <span data-testid="notif-count" className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 bg-neon-pink text-black font-mono-tech text-[10px] font-bold rounded-full flex items-center justify-center">{unread}</span>}
        </button>
        {open && (
          <div className="absolute right-0 mt-2 w-80 glass border border-white/12 max-h-[70vh] overflow-y-auto" data-testid="notifications-panel">
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <span className="font-mono-tech text-[10px] uppercase tracking-widest text-white/50">Notifications</span>
              <div className="flex items-center gap-3">
                {unread > 0 && <button onClick={markAll} className="text-white/40 hover:text-neon-turquoise" title="Mark all read" data-testid="notif-mark-all"><CheckCheck size={15} /></button>}
                <button onClick={() => setOpen(false)} className="text-white/40 hover:text-neon-pink"><X size={15} /></button>
              </div>
            </div>
            {items.length === 0 ? (
              <div className="px-4 py-8 text-center font-mono-tech text-[11px] text-white/30">No notifications yet</div>
            ) : items.map((n) => {
              const cfg = ICONS[n.type] || { i: Bell, c: "#9aa" };
              const Ico = cfg.i;
              return (
                <div key={n.id} onClick={() => { markRead(n.id); if (n.type === "auto_recharge" && n.meta?.pack_id) setConfirm(n); }}
                  data-testid={`notif-${n.type}`}
                  className={`px-4 py-3 border-b border-white/5 cursor-pointer hover:bg-white/[0.03] ${n.read ? "opacity-55" : ""}`}>
                  <div className="flex gap-3">
                    <Ico size={15} style={{ color: cfg.c }} className="shrink-0 mt-0.5" />
                    <div className="min-w-0">
                      <div className="font-mono-tech text-[12px] text-white/85">{n.title}</div>
                      <div className="font-mono-tech text-[11px] text-white/45 mt-0.5">{n.body}</div>
                    </div>
                    {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-neon-turquoise shrink-0 mt-1.5 ml-auto" />}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {confirm && (
        <div className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" data-testid="auto-recharge-confirm">
          <div className="glass clip-corner border border-white/12 max-w-md w-full p-6" style={{ borderTop: "2px solid #00f0ff" }}>
            <div className="flex items-center gap-2 mb-2"><RefreshCw size={18} className="text-neon-turquoise" /><h3 className="font-display font-bold text-lg">Auto-Recharge Ready</h3></div>
            <p className="font-mono-tech text-[12px] text-white/60 leading-relaxed">
              Your balance dropped below your threshold. Confirm to add <b className="text-neon-turquoise">+{confirm.meta?.credits?.toLocaleString()} credits</b> for <b>${Number(confirm.meta?.price).toFixed(2)}</b>. No charge happens until you approve.
            </p>
            <div className="flex gap-3 mt-6">
              <NeonButton accent="turquoise" onClick={doRecharge} disabled={loading} data-testid="confirm-recharge-btn">
                {loading ? <><Loader2 size={14} className="animate-spin" /> Redirecting…</> : <><Zap size={13} /> Confirm & Pay</>}
              </NeonButton>
              <NeonButton onClick={() => setConfirm(null)} data-testid="dismiss-recharge-btn">Not Now</NeonButton>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
